import React, { useRef } from 'react';
import { Mic } from 'lucide-react';
import ParalinguisticTags from './ParalinguisticTags';

function TTSForm({ onSubmit, loading, formData, setFormData, credits = 0, jobProgress = 0, jobStatus = '' }) {
  const textareaRef = useRef(null);
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? parseFloat(value) : value,
    });
  };

  const textLength = formData.text.length;
  const hasEnoughCredits = credits >= textLength;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.text.trim() && hasEnoughCredits) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Text Input Section */}
      <div className="text-input-section-content">
        <h2 className="section-title mb-2">
          <Mic size={20} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
          Generate Speech
        </h2>
      </div>

      {/* Text Input */}
      <div className="textarea-wrapper">
        <textarea
          ref={textareaRef}
          id="text"
          name="text"
          value={formData.text}
          onChange={handleChange}
          placeholder="Enter the text you want to convert to speech..."
          rows="12"
          maxLength="20000"
          required
          className="text-textarea w-full resize-none"
        />
        {/* Cost and Character Limit Row - Moved inside to remove gap */}
        <div className="cost-char-info-row">
        {textLength > 0 && (
          <p className="cost-info" style={{
            color: hasEnoughCredits ? 'var(--text-primary)' : 'var(--error-color)',
          }}>
            Cost: {textLength} credits {!hasEnoughCredits && `(Need ${textLength - credits} more)`}
          </p>
        )}
        <p className="char-count">
          {formData.text.length}/20000 characters
        </p>
        </div>
      </div>

      {/* Hidden Voice Controls - Will be in separate section */}
      <input
        id="voice"
        name="voice"
        value={formData.voice}
        onChange={handleChange}
        type="hidden"
      />

      <input
        id="temperature"
        type="hidden"
        name="temperature"
        value={formData.temperature}
      />

      {/* Tags and Generate Speech Button Row */}
      <div className="tags-button-row">
        <ParalinguisticTags
          textareaRef={textareaRef}
          formData={formData}
          setFormData={setFormData}
        />
        <button
          type="submit"
          disabled={loading || !formData.text.trim() || !hasEnoughCredits}
          className="btn-submit"
          title={!hasEnoughCredits ? `Insufficient credits. Need ${textLength - credits} more.` : ''}
        >
          {loading ? (
            <span className="flex items-center justify-center flex-col">
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {jobStatus || 'Generating...'}
              </div>
              {jobProgress > 0 && (
                <div className="w-full mt-2">
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-gold h-2 rounded-full transition-all duration-300"
                      style={{ width: `${jobProgress}%` }}
                    ></div>
                  </div>
                  <span className="text-xs mt-1">{jobProgress}%</span>
                </div>
              )}
            </span>
          ) : (
            'Generate Speech'
          )}
        </button>
      </div>
    </form>
  );
}

export default TTSForm;
